import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { LoadingBlock, PageIntro, CtaLink, Reveal } from "@/components/public/primitives";
import { Hero, Stats, SelectedWork, Services, About, Ceo, Team, Process, Pricing, Faq, ContactCta, WorkCard } from "@/components/public/sections";
import InquiryForm from "@/components/public/InquiryForm";
import { fileUrl } from "@/lib/api";

function useSiteData() {
  const q = useSite();
  if (q.isLoading) return { loading: true };
  if (q.isError || !q.data) return { error: true };
  return { d: q.data };
}

function SiteError() {
  return <div className="container-x pt-40 pb-20 text-center" data-testid="site-error"><p className="eyebrow">Error</p><h1 className="display-lg mt-4">Content unavailable</h1><p className="mt-4 text-[var(--text-dim)]">We could not load the site content. Please refresh in a moment.</p></div>;
}

export function HomePage() {
  const { loading, error, d } = useSiteData();
  if (loading) return <LoadingBlock />;
  if (error) return <SiteError />;
  const p = d.pages;
  return (
    <div data-testid="home-page">
      <Hero hero={p.hero} /><Stats stats={p.stats} /><SelectedWork work={d.work} /><Services services={d.services} />
      <About about={p.about} /><Ceo ceo={p.ceo} /><Team team={p.team} members={d.team} /><Process process={p.process} />
      <Pricing pricing={p.pricing} /><Faq faqs={d.faqs} /><ContactCta contact={p.contact} />
    </div>
  );
}

export function WorkPage() {
  const { loading, error, d } = useSiteData();
  if (loading) return <LoadingBlock />;
  if (error) return <SiteError />;
  return (
    <div data-testid="work-page">
      <PageIntro label="Selected Work" title={"WORK THAT\nSPEAKS FOR ITSELF."} description="Concept projects and portfolio examples that show how we think about product, engineering and interface quality. Real client work is published here when approved." />
      <SelectedWork work={d.work} full />
      <ContactCta contact={d.pages.contact} />
    </div>
  );
}

export function WorkDetailPage() {
  const { id } = useParams();
  const { loading, error, d } = useSiteData();
  if (loading) return <LoadingBlock />;
  if (error) return <SiteError />;
  const item = d.work.find((w) => w.id === id);
  if (!item) return <div className="container-x pt-40 pb-24" data-testid="work-not-found"><p className="eyebrow">404</p><h1 className="display-lg mt-4">Project not found</h1><Link to="/work" className="btn-ghost mt-8">Back to work</Link></div>;
  const idx = d.work.indexOf(item);
  const others = d.work.filter((w) => w.id !== id).slice(0, 2);
  return (
    <div data-testid="work-detail-page">
      <section className="border-b hairline pt-32 pb-16 sm:pt-40">
        <div className="container-x">
          <Link to="/work" className="eyebrow inline-flex items-center gap-2 hover:text-white" data-testid="work-back"><ArrowLeft className="h-3.5 w-3.5" /> All work</Link>
          <p className="eyebrow mt-10"><span className="text-[var(--accent)]">{String(idx + 1).padStart(2, "0")}</span> — {item.category} · <span className="text-white">{item.label}</span></p>
          <h1 className="display-xl mt-6" data-testid="work-detail-title">{item.title}</h1>
          <div className="mt-12 grid gap-10 lg:grid-cols-12">
            <div className="surface aspect-[16/9] overflow-hidden rounded-lg lg:col-span-8">{item.image ? <img src={fileUrl(item.image)} alt={item.title} className="h-full w-full object-cover" /> : <div className="grain relative h-full"><div className="glow-violet absolute -right-20 -top-20 h-96 w-96" /></div>}</div>
            <div className="lg:col-span-4">
              <p className="text-base leading-relaxed text-[var(--text-dim)] md:text-lg">{item.description}</p>
              {item.details && <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-[var(--text-dim)]">{item.details}</p>}
              <div className="mt-8 border-t hairline pt-6"><p className="eyebrow mb-3">Scope</p><div className="flex flex-wrap gap-2">{(item.tags || []).map((t) => <span key={t} className="rounded-full border border-[var(--line-strong)] px-3 py-1 text-xs text-white">{t}</span>)}</div></div>
              {item.year && <div className="mt-6"><p className="eyebrow mb-1">Year</p><p className="text-white">{item.year}</p></div>}
              {item.link && <a href={item.link} target="_blank" rel="noreferrer" className="btn-ghost mt-8">Visit project</a>}
            </div>
          </div>
        </div>
      </section>
      {others.length > 0 && <section className="border-b hairline py-20"><div className="container-x"><p className="eyebrow mb-8">More work</p><div className="grid gap-6 md:grid-cols-2">{others.map((w, i) => <WorkCard key={w.id} item={w} index={d.work.indexOf(w)} />)}</div></div></section>}
      <ContactCta contact={d.pages.contact} />
    </div>
  );
}

export function ServicesPage() {
  const { loading, error, d } = useSiteData();
  if (loading) return <LoadingBlock />;
  if (error) return <SiteError />;
  return (
    <div data-testid="services-page">
      <PageIntro label="Services" title={"ONE TEAM.\nEVERY DISCIPLINE."} description="We cover the full product lifecycle — strategy, design, engineering, quality assurance and launch — so you work with a single accountable team." />
      <Services services={d.services} full />
      <Process process={d.pages.process} />
      <Pricing pricing={d.pages.pricing} />
      <ContactCta contact={d.pages.contact} />
    </div>
  );
}

export function AboutPage() {
  const { loading, error, d } = useSiteData();
  if (loading) return <LoadingBlock />;
  if (error) return <SiteError />;
  return (
    <div data-testid="about-page">
      <PageIntro label="About" title={d.pages.about.title} description={d.pages.about.paragraphs?.[0]} />
      <section className="border-b hairline py-20 sm:py-28"><div className="container-x grid gap-10 lg:grid-cols-12"><div className="lg:col-span-4"><p className="eyebrow">Who we are</p></div><div className="space-y-6 lg:col-span-7 lg:col-start-6">{d.pages.about.paragraphs?.slice(1).map((p, i) => <Reveal key={i}><p className="text-lg leading-relaxed text-white md:text-xl">{p}</p></Reveal>)}</div></div></section>
      <Stats stats={d.pages.stats} />
      <Ceo ceo={d.pages.ceo} />
      <Team team={d.pages.team} members={d.team} />
      <ContactCta contact={d.pages.contact} />
    </div>
  );
}

export function TeamPage() {
  const { loading, error, d } = useSiteData();
  if (loading) return <LoadingBlock />;
  if (error) return <SiteError />;
  return (
    <div data-testid="team-page">
      <PageIntro label="Team" title={d.pages.team.title} description="A cross-functional team across development, design, engineering and quality assurance, led by CEO MD Rahi." />
      <Team team={d.pages.team} members={d.team} full />
      {!d.team.length && <section className="border-b hairline py-16"><div className="container-x"><p className="text-[var(--text-dim)]" data-testid="team-empty">Individual team profiles are published by the RAHI DEV administration as they become available.</p></div></section>}
      <Ceo ceo={d.pages.ceo} />
      <ContactCta contact={d.pages.contact} />
    </div>
  );
}

export function ProcessPage() {
  const { loading, error, d } = useSiteData();
  if (loading) return <LoadingBlock />;
  if (error) return <SiteError />;
  return (
    <div data-testid="process-page">
      <PageIntro label="Process" title={"HOW WE TAKE A\nPRODUCT TO LAUNCH."} description="Six clear steps. Every client gets access to a private portal where progress, milestones, files and approvals are tracked in one place." />
      <Process process={d.pages.process} full />
      <Faq faqs={d.faqs} />
      <ContactCta contact={d.pages.contact} />
    </div>
  );
}

export function ContactPage() {
  const { loading, error, d } = useSiteData();
  if (loading) return <LoadingBlock />;
  if (error) return <SiteError />;
  const s = d.settings;
  return (
    <div data-testid="contact-page">
      <section className="relative overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28">
        <div className="glow-violet pointer-events-none absolute -top-40 right-[-10%] h-[520px] w-[520px]" />
        <div className="container-x relative grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="eyebrow rise">Project Inquiry</p>
            <h1 className="display-lg rise rise-1 mt-6 whitespace-pre-line">{d.pages.contact.title}</h1>
            <p className="rise rise-2 mt-6 text-base text-[var(--text-dim)] md:text-lg">{d.pages.contact.description}</p>
            <div className="rise rise-3 mt-10 space-y-5 border-t hairline pt-8 text-sm">
              <div><p className="eyebrow mb-1">Starting point</p><p className="text-white">{d.pages.pricing.amount} per project</p></div>
              {s.email && <div><p className="eyebrow mb-1">Email</p><a href={`mailto:${s.email}`} className="text-white hover:text-[var(--accent)]">{s.email}</a></div>}
              {s.whatsapp && <div><p className="eyebrow mb-1">WhatsApp</p><p className="text-white">{s.whatsapp}</p></div>}
              <div><p className="eyebrow mb-1">Existing client?</p><Link to="/login" className="text-white hover:text-[var(--accent)]" data-testid="contact-client-login">Log in to your project portal →</Link></div>
            </div>
          </div>
          <div className="lg:col-span-8"><InquiryForm /></div>
        </div>
      </section>
    </div>
  );
}

export function LegalPage({ pageKey }) {
  const { loading, error, d } = useSiteData();
  if (loading) return <LoadingBlock />;
  if (error) return <SiteError />;
  const page = d.pages[pageKey] || {};
  return (
    <div data-testid={`${pageKey}-page`}>
      <PageIntro label="Legal" title={page.title} description={page.updated ? `Last updated: ${page.updated}` : undefined} />
      <section className="py-16 sm:py-24"><div className="container-x max-w-3xl prose-dark">{(page.body || "").split("\n\n").map((para, i) => {
        const lines = para.split("\n");
        const isHeading = lines.length > 1 && lines[0].length < 60;
        return isHeading ? <div key={i} className="mb-8"><h2 className="mb-3 font-display text-lg font-bold uppercase text-white md:text-lg">{lines[0]}</h2><p className="text-[var(--text-dim)] leading-relaxed whitespace-pre-line">{lines.slice(1).join("\n")}</p></div> : <p key={i}>{para}</p>;
      })}</div></section>
    </div>
  );
}

export function NotFoundPage() {
  return (
    <div className="container-x flex min-h-screen flex-col items-start justify-center py-32" data-testid="not-found-page">
      <p className="eyebrow">404</p>
      <h1 className="display-xl mt-6">PAGE<br />NOT FOUND.</h1>
      <p className="mt-6 max-w-md text-[var(--text-dim)]">The page you are looking for does not exist or has been moved.</p>
      <div className="mt-10"><CtaLink to="/" testId="not-found-home">Back to home</CtaLink></div>
    </div>
  );
}
